import './ChangeTimeModal.scss';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import Error from '../../../../common/Error/Error';
import LoadingSpinner from '../../../../common/LoadingSpinner/LoadingSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faX } from '@fortawesome/free-solid-svg-icons';
import GoodsIn from '../../../../api/controllers/GoodsIn/GoodsIn';
import GoodsOut from '../../../../api/controllers/GoodsOut/GoodsOut';

// General ChangeTimeModal Component
const ChangeTimeModal = ({
  setModalOpen,
  modalType,
  movement,
  movementType,
  category,
  getMovements,
}) => {
  const [error, setError] = useState();
  const [loading, setLoading] = useState();

  const [updatedDate, setUpdatedDate] = useState('');
  const [updatedHour, setUpdatedHour] = useState('');

  const updateExpectedTime = () => {
    if (
      movementType == 'collections' ||
      movementType == 'imports' ||
      movementType == 'subcontracts_in'
    ) {
      setLoading(true);
      GoodsIn.updateExpectedArrivalTime(category, movement?.id, updatedDate, updatedHour)
        .then(() => {
          setLoading(false);
          getMovements();
          setModalOpen(false);
        })
        .catch(e => {
          setLoading(false);
          setError(e.errors || e.error);
        });
    }
    if (
      movementType == 'deliveries' ||
      movementType == 'exports' ||
      movementType == 'subcontracts_out'
    ) {
      setLoading(true);
      GoodsOut.updateExpectedArrivalTime(category, movement?.id, updatedDate, updatedHour)
        .then(() => {
          setLoading(false);
          getMovements();
          setModalOpen(false);
        })
        .catch(e => {
          setLoading(false);
          setError(e.errors || e.error);
        });
    }
  };

  const updateActualTime = () => {
    if (updatedDate == '' || updatedHour == '') {
      setError('Please enter a valid date and time');
    } else {
      const combinedDateHour = new Date(`${updatedDate}, ${updatedHour}`);
      if (
        movementType == 'collections' ||
        movementType == 'imports' ||
        movementType == 'subcontracts_in'
      ) {
        setLoading(true);
        GoodsIn.updateArrivalTime(category, combinedDateHour, movement?.id)
          .then(() => {
            setLoading(false);
            getMovements();
            setModalOpen(false);
          })
          .catch(e => {
            setLoading(false);
            setError(e.errors || e.error);
          });
      }
      if (
        movementType == 'deliveries' ||
        movementType == 'exports' ||
        movementType == 'subcontracts_out'
      ) {
        setLoading(true);
        GoodsOut.updateArrivalTime(category, combinedDateHour, movement?.id)
          .then(() => {
            setLoading(false);
            getMovements();
            setModalOpen(false);
          })
          .catch(e => {
            setLoading(false);
            setError(e.errors || e.error);
          });
      }
    }
  };

  const resetTime = () => {
    if (modalType == 'estimated') {
      setUpdatedDate(movement?.originalArrivalDate);
      setUpdatedHour(movement?.originalArrivalHour);
    } else {
      setUpdatedDate(movement?.currentExpectedArrivalDate);
      setUpdatedHour(movement?.currentExpectedArrivalHour);
    }
  };

  const clearTime = () => {
    setUpdatedDate('');
    setUpdatedHour('');
  };

  useEffect(() => {
    if (modalType == 'estimated') {
      setUpdatedDate(movement?.currentExpectedArrivalDate || '');
      setUpdatedHour(movement?.currentExpectedArrivalHour || '');
    } else {
      const date = movement?.actualArrivalTime?.split('T')[0];
      const hour = movement?.actualArrivalTime?.split('T')[1]?.split(':')[0];
      const minute = movement?.actualArrivalTime?.split('T')[1]?.split(':')[1];

      setUpdatedDate(date || '');
      setUpdatedHour(hour && minute ? `${hour}:${minute}` : '');
    }
  }, []);

  return (
    <div className="update_time_container">
      {loading && <LoadingSpinner />}
      {error && (
        <Error
          errorMessages={error}
          type={'error'}
          errorTitle={
            Array.isArray(error)
              ? `${error.length} error${error.length > 1 ? 's' : ''} occurred: `
              : ''
          }
        />
      )}
      {modalType == 'estimated' ? (
        <div className="update_time_form">
          <div className="orginal_estimated_time">
            <div className="label">Original Estimate</div>
            <input
              data-testid="original_estimate_date_picker"
              className="date_picker"
              type={'date'}
              disabled
              value={movement?.originalArrivalDate}
            />
            <div className="label_at">at</div>
            <div className="time_and_button">
              <input
                data-testid="original_estimate_time_picker"
                className="time_picker"
                type={'time'}
                disabled
                value={movement?.originalArrivalHour}
              />
              <button
                className="reset_button"
                onClick={() => resetTime()}
                data-testid="undo_button"
              >
                <FontAwesomeIcon icon={faUndo} className="undo_icon" />
              </button>
            </div>
          </div>
          <div className="current_estimated_time">
            <div className="label">Current Estimate</div>
            <input
              data-testid="current_estimate_date_picker"
              className="date_picker"
              type={'date'}
              value={updatedDate}
              onChange={e => setUpdatedDate(e.target.value)}
            />
            <div className="label_at">at</div>
            <div className="time_and_button">
              <input
                data-testid="current_estimate_time_picker"
                className="time_picker"
                type={'time'}
                value={updatedHour}
                onChange={e => setUpdatedHour(e.target.value)}
              />
              <button
                className="clear_button"
                onClick={() => clearTime()}
                data-testid="clear_button"
              >
                <FontAwesomeIcon icon={faX} className="clear_icon" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="update_time_form">
          <div className="orginal_estimated_time">
            <div className="label">Estimated Arrival</div>
            <input
              data-testid="estimate_arrival_date_picker"
              className="date_picker"
              type={'date'}
              disabled
              value={movement?.currentExpectedArrivalDate}
            />
            <div className="label_at">at</div>
            <div className="time_and_button">
              <input
                data-testid="estimate_arrival_time_picker"
                className="time_picker"
                type={'time'}
                disabled
                value={movement?.currentExpectedArrivalHour}
              />
              <button
                className="reset_button"
                onClick={() => resetTime()}
                data-testid="undo_button"
              >
                <FontAwesomeIcon icon={faUndo} className="undo_icon" />
              </button>
            </div>
          </div>
          <div className="actual_time">
            <div className="label">Actual Arrival</div>
            <input
              data-testid="actual_arrival_date_picker"
              className="date_picker"
              type={'date'}
              value={updatedDate}
              onChange={e => setUpdatedDate(e.target.value)}
            />
            <div className="label_at">at</div>
            <div className="time_and_button">
              <input
                data-testid="actual_arrival_time_picker"
                className="time_picker"
                type={'time'}
                value={updatedHour}
                onChange={e => setUpdatedHour(e.target.value)}
              />
              <button
                className="clear_button"
                onClick={() => clearTime()}
                data-testid="clear_button"
              >
                <FontAwesomeIcon icon={faX} className="clear_icon" />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="update_time_buttons">
        <button
          className="update_button"
          onClick={modalType == 'estimated' ? () => updateExpectedTime() : () => updateActualTime()}
        >
          Update
        </button>
        <button className="close_button" onClick={() => setModalOpen(false)}>
          Close
        </button>
      </div>
    </div>
  );
};

ChangeTimeModal.propTypes = {
  setModalOpen: PropTypes.func,
  modalType: PropTypes.string,
  movement: PropTypes.object,
  movementType: PropTypes.string,
  category: PropTypes.string,
  getMovements: PropTypes.func,
};

export default ChangeTimeModal;
